import { useState, useMemo } from 'react';
import './Textdiffchecker.css';

const TextDiffChecker = () => {
  const [originalText, setOriginalText] = useState('');
  const [modifiedText, setModifiedText] = useState('');
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [ignoreCase, setIgnoreCase] = useState(false);

  // Compute word-level differences within a line
  const computeWordDiff = (text1, text2) => {
    const words1 = text1.split(/(\s+)/);
    const words2 = text2.split(/(\s+)/);
    
    const lcs = getLCS(words1, words2);
    const diff = [];
    let i = 0, j = 0, lcsIndex = 0;

    while (i < words1.length || j < words2.length) {
      if (lcsIndex < lcs.length && i < words1.length && words1[i] === lcs[lcsIndex]) {
        diff.push({ type: 'equal', value: words1[i] });
        i++;
        j++;
        lcsIndex++;
      } else if (i < words1.length && (lcsIndex >= lcs.length || words1[i] !== lcs[lcsIndex])) {
        diff.push({ type: 'delete', value: words1[i] });
        i++;
      } else if (j < words2.length) {
        diff.push({ type: 'insert', value: words2[j] });
        j++;
      }
    }

    return diff;
  };

  // LCS algorithm
  const getLCS = (arr1, arr2) => {
    const m = arr1.length;
    const n = arr2.length;
    const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (arr1[i - 1] === arr2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    const lcs = [];
    let i = m, j = n;
    while (i > 0 && j > 0) {
      if (arr1[i - 1] === arr2[j - 1]) {
        lcs.unshift(arr1[i - 1]);
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }

    return lcs;
  };

  const computeLineDiff = useMemo(() => {
    if (!originalText && !modifiedText) return null;

    let text1 = originalText;
    let text2 = modifiedText;

    if (ignoreCase) {
      text1 = text1.toLowerCase();
      text2 = text2.toLowerCase();
    }

    if (ignoreWhitespace) {
      text1 = text1.replace(/\s+/g, ' ').trim();
      text2 = text2.replace(/\s+/g, ' ').trim();
    }

    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');
    const originalLines = originalText.split('\n');
    const modifiedLines = modifiedText.split('\n');

    const lcs = getLCS(lines1, lines2);
    const diff = [];
    let i = 0, j = 0, lcsIndex = 0;

    while (i < lines1.length || j < lines2.length) {
      if (lcsIndex < lcs.length && i < lines1.length && lines1[i] === lcs[lcsIndex]) {
        diff.push({
          type: 'equal',
          original: originalLines[i],
          modified: modifiedLines[j],
          originalLine: i + 1,
          modifiedLine: j + 1
        });
        i++;
        j++;
        lcsIndex++;
      } else if (i < lines1.length && (lcsIndex >= lcs.length || lines1[i] !== lcs[lcsIndex])) {
        // Check if next line in text2 matches (modified line)
        if (j < lines2.length && lcsIndex < lcs.length && lines2[j] !== lcs[lcsIndex]) {
          // Both lines differ - this is a modification
          diff.push({
            type: 'modified',
            original: originalLines[i],
            modified: modifiedLines[j],
            originalLine: i + 1,
            modifiedLine: j + 1,
            wordDiff: computeWordDiff(originalLines[i], modifiedLines[j])
          });
          i++;
          j++;
        } else {
          // Pure deletion
          diff.push({
            type: 'delete',
            original: originalLines[i],
            originalLine: i + 1
          });
          i++;
        }
      } else if (j < lines2.length) {
        diff.push({
          type: 'insert',
          modified: modifiedLines[j],
          modifiedLine: j + 1
        });
        j++;
      }
    }

    return diff;
  }, [originalText, modifiedText, ignoreWhitespace, ignoreCase]);

  const getStats = () => {
    if (!computeLineDiff) {
      return { removals: 0, additions: 0 };
    }

    const removals = computeLineDiff.filter(d => d.type === 'delete').length;
    const additions = computeLineDiff.filter(d => d.type === 'insert').length;

    return { removals, additions };
  };

  const copyLine = (text) => {
    navigator.clipboard.writeText(text);
  };

  const clearAll = () => {
    setOriginalText('');
    setModifiedText('');
  };

  const swapTexts = () => {
    const temp = originalText;
    setOriginalText(modifiedText);
    setModifiedText(temp);
  };

  const saveAsDiff = () => {
    if (!computeLineDiff) return;

    let diffContent = 'TEXT DIFFERENCE REPORT\n';
    diffContent += '='.repeat(80) + '\n\n';
    diffContent += `Generated: ${new Date().toLocaleString()}\n`;
    diffContent += `Total Removals: ${stats.removals}\n`;
    diffContent += `Total Additions: ${stats.additions}\n`;
    diffContent += '='.repeat(80) + '\n\n';

    diffContent += 'ORIGINAL TEXT:\n';
    diffContent += '-'.repeat(80) + '\n';
    diffContent += originalText + '\n\n';

    diffContent += 'MODIFIED TEXT:\n';
    diffContent += '-'.repeat(80) + '\n';
    diffContent += modifiedText + '\n\n';

    diffContent += 'DIFFERENCES:\n';
    diffContent += '-'.repeat(80) + '\n\n';

    computeLineDiff.forEach((line, idx) => {
      if (line.type === 'delete') {
        diffContent += `- [Line ${line.originalLine}] DELETED: ${line.original}\n`;
      } else if (line.type === 'insert') {
        diffContent += `+ [Line ${line.modifiedLine}] ADDED: ${line.modified}\n`;
      } else if (line.type === 'modified') {
        diffContent += `~ [Line ${line.originalLine}→${line.modifiedLine}] MODIFIED:\n`;
        diffContent += `  OLD: ${line.original}\n`;
        diffContent += `  NEW: ${line.modified}\n`;
      }
    });

    const blob = new Blob([diffContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `text-diff-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stats = getStats();

  return (
    <div className="diff-checker-container">
      {/* Header */}
      <div className="diff-header">
        <h1 className="diff-title">Text Diff Checker</h1>
        <div className="diff-actions">
          <button className="diff-btn" onClick={swapTexts}>
            ⇄ Swap
          </button>
          <button className="diff-btn diff-btn-danger" onClick={clearAll}>
            Clear
          </button>
          <button className="diff-btn diff-btn-primary" onClick={saveAsDiff}>
            💾 Save Report
          </button>
        </div>
      </div>

      {/* Options */}
      <div className="diff-options">
        <label className="diff-checkbox">
          <input
            type="checkbox"
            checked={ignoreWhitespace}
            onChange={(e) => setIgnoreWhitespace(e.target.checked)}
          />
          <span>Ignore Whitespace</span>
        </label>
        <label className="diff-checkbox">
          <input
            type="checkbox"
            checked={ignoreCase}
            onChange={(e) => setIgnoreCase(e.target.checked)}
          />
          <span>Ignore Case</span>
        </label>
      </div>

      {/* Input Areas */}
      <div className="diff-input-grid">
        <div className="diff-input-panel">
          <div className="diff-input-title">Original Text</div>
          <textarea
            className="diff-textarea"
            value={originalText}
            onChange={(e) => setOriginalText(e.target.value)}
            placeholder="Paste original text here..."
            spellCheck={false}
          />
        </div>

        <div className="diff-input-panel">
          <div className="diff-input-title">Modified Text</div>
          <textarea
            className="diff-textarea"
            value={modifiedText}
            onChange={(e) => setModifiedText(e.target.value)}
            placeholder="Paste modified text here..."
            spellCheck={false}
          />
        </div>
      </div>

      {/* Diff Results */}
      {computeLineDiff && (originalText || modifiedText) && (
        <div className="diff-results">
          {/* Stats Bar */}
          <div className="diff-stats">
            <div className="diff-stats-left">
              {stats.removals > 0 && (
                <div className="diff-stat-item diff-stat-deletion">
                  <span>− {stats.removals} removal{stats.removals !== 1 ? 's' : ''}</span>
                </div>
              )}
              {stats.additions > 0 && (
                <div className="diff-stat-item diff-stat-addition">
                  <span>+ {stats.additions} addition{stats.additions !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>

          {/* Split View */}
          <div className="diff-view-split">
            {/* Left Panel - Deletions */}
            <div className="diff-panel">
              {computeLineDiff.map((line, idx) => {
                if (line.type === 'delete' || line.type === 'modified') {
                  return (
                    <div key={`left-${idx}`} className={`diff-row ${line.type === 'delete' ? 'deletion' : 'modification'}`}>
                      <div className="diff-ln">{line.originalLine}</div>
                      <div className="diff-line-content">
                        {line.type === 'delete' ? (
                          <span className="deletion-text">{line.original}</span>
                        ) : (
                          <span>
                            {line.wordDiff?.map((word, widx) => (
                              <span
                                key={widx}
                                className={word.type === 'delete' ? 'word-deletion' : word.type === 'equal' ? '' : 'word-hidden'}
                              >
                                {word.type !== 'insert' && word.value}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                } else if (line.type === 'equal') {
                  return (
                    <div key={`left-${idx}`} className="diff-row">
                      <div className="diff-ln">{line.originalLine}</div>
                      <div className="diff-line-content">{line.original}</div>
                    </div>
                  );
                } else {
                  return (
                    <div key={`left-${idx}`} className="diff-row empty">
                      <div className="diff-ln"></div>
                      <div className="diff-line-content"></div>
                    </div>
                  );
                }
              })}
            </div>

            {/* Right Panel - Additions */}
            <div className="diff-panel">
              {computeLineDiff.map((line, idx) => {
                if (line.type === 'insert' || line.type === 'modified') {
                  return (
                    <div key={`right-${idx}`} className={`diff-row ${line.type === 'insert' ? 'addition' : 'modification'}`}>
                      <div className="diff-ln">{line.modifiedLine}</div>
                      <div className="diff-line-content">
                        {line.type === 'insert' ? (
                          <span className="addition-text">{line.modified}</span>
                        ) : (
                          <span>
                            {line.wordDiff?.map((word, widx) => (
                              <span
                                key={widx}
                                className={word.type === 'insert' ? 'word-addition' : word.type === 'equal' ? '' : 'word-hidden'}
                              >
                                {word.type !== 'delete' && word.value}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                } else if (line.type === 'equal') {
                  return (
                    <div key={`right-${idx}`} className="diff-row">
                      <div className="diff-ln">{line.modifiedLine}</div>
                      <div className="diff-line-content">{line.modified || line.original}</div>
                    </div>
                  );
                } else {
                  return (
                    <div key={`right-${idx}`} className="diff-row empty">
                      <div className="diff-ln"></div>
                      <div className="diff-line-content"></div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
      )}

      {!originalText && !modifiedText && (
        <div className="diff-empty">
          <div className="diff-empty-icon">📄</div>
          <h3>No text to compare</h3>
          <p>Enter text in both fields above to see the differences</p>
        </div>
      )}
    </div>
  );
};

export default TextDiffChecker;